//
// # CRDT Infrastructure
//
// Core types and utilities for Conflict-free Replicated Data Types (CRDT)
// used in multi-user collaborative editing.
//
// This module provides:
// - LamportTimestamp for causal ordering of operations
// - CRDTEntity wrapper for adding CRDT metadata to entities
// - CRDTOp for operation-based CRDT wire protocol
// - Runtime validators for wire format types
//

import typia from "typia";
import { Validator } from "./validate";

/**
 * Lamport timestamp for establishing causal ordering of concurrent operations.
 *
 * Uses a logical clock counter combined with clientId as a tie-breaker
 * to ensure total ordering even for truly concurrent events.
 */
export interface LamportTimestamp {
  /** Monotonically increasing logical clock counter */
  counter: number;
  /** Client identifier used as tie-breaker for concurrent events */
  clientId: string;
}

/**
 * Compare two Lamport timestamps.
 * @returns negative if a < b, zero if a == b, positive if a > b
 */
export function compareTimestamp(
  a: LamportTimestamp,
  b: LamportTimestamp
): number {
  if (a.counter !== b.counter) {
    return a.counter - b.counter;
  }
  // Tie-breaker: deterministic lexicographic comparison of clientId
  // (avoid localeCompare which can vary by locale)
  return a.clientId < b.clientId ? -1 : a.clientId > b.clientId ? 1 : 0;
}

/**
 * Returns the later of two timestamps.
 */
export function maxTimestamp(
  a: LamportTimestamp,
  b: LamportTimestamp
): LamportTimestamp {
  return compareTimestamp(a, b) >= 0 ? a : b;
}

/**
 * Create an initial timestamp for a new client.
 */
export function createInitialTimestamp(clientId: string): LamportTimestamp {
  return { counter: 0, clientId };
}

/**
 * Advance a timestamp by incrementing its counter.
 * Used when the local client performs an operation.
 */
export function tickTimestamp(ts: LamportTimestamp): LamportTimestamp {
  return { counter: ts.counter + 1, clientId: ts.clientId };
}

/**
 * Update local timestamp based on a received remote timestamp.
 * Ensures the local clock is always ahead of any observed remote clock.
 */
export function receiveTimestamp(
  local: LamportTimestamp,
  remote: LamportTimestamp
): LamportTimestamp {
  return {
    counter: Math.max(local.counter, remote.counter) + 1,
    clientId: local.clientId,
  };
}

/**
 * CRDT wrapper for an entity, adding metadata for conflict resolution.
 *
 * Uses Last-Write-Wins (LWW) semantics at the entity level.
 * Tombstones are used for deletions to handle concurrent delete/update conflicts.
 */
export interface CRDTEntity<T> {
  /** The actual entity value */
  value: T;

  /** Timestamp of the last modification */
  lastModified: LamportTimestamp;

  /** Client that made the last modification */
  modifiedBy: string;

  /** Whether this entity has been deleted (tombstone) */
  deleted: boolean;

  /** Timestamp when the entity was deleted (if deleted) */
  deletedAt?: LamportTimestamp;
}

/**
 * Create a new CRDT entity wrapper.
 */
export function createCRDTEntity<T>(
  value: T,
  timestamp: LamportTimestamp,
  clientId: string
): CRDTEntity<T> {
  return {
    value,
    lastModified: timestamp,
    modifiedBy: clientId,
    deleted: false,
  };
}

/**
 * Mark a CRDT entity as deleted (tombstone).
 */
export function deleteCRDTEntity<T>(
  entity: CRDTEntity<T>,
  timestamp: LamportTimestamp
): CRDTEntity<T> {
  return {
    ...entity,
    deleted: true,
    deletedAt: timestamp,
  };
}

/**
 * Merge two CRDT entities using LWW semantics.
 *
 * Rules:
 * - If both deleted: keep the one with later deletedAt
 * - If one deleted: delete wins unless the other was modified after deletion
 * - If both alive: keep the one with later lastModified
 */
export function mergeCRDTEntities<T>(
  local: CRDTEntity<T>,
  remote: CRDTEntity<T>
): CRDTEntity<T> {
  // Both deleted: keep later deletion
  if (local.deleted && remote.deleted) {
    return compareTimestamp(local.deletedAt!, remote.deletedAt!) >= 0
      ? local
      : remote;
  }

  // Local deleted, remote alive
  if (local.deleted) {
    // Resurrection: remote edit happened after local delete
    if (compareTimestamp(remote.lastModified, local.deletedAt!) > 0) {
      return remote;
    }
    return local; // Delete stands
  }

  // Remote deleted, local alive
  if (remote.deleted) {
    // Resurrection: local edit happened after remote delete
    if (compareTimestamp(local.lastModified, remote.deletedAt!) > 0) {
      return local;
    }
    return remote; // Delete stands
  }

  // Both alive: take newer modification
  return compareTimestamp(local.lastModified, remote.lastModified) >= 0
    ? local
    : remote;
}

//
// # CRDT Operation Types
//
// Wire format for CRDT operations sent between clients and server.
//

/**
 * A CRDT operation for the wire protocol.
 */
export interface CRDTOp {
  /** Unique operation ID (UUID) */
  opId: string;

  /** Originating client */
  clientId: string;

  /** Operation timestamp */
  timestamp: LamportTimestamp;

  /** Content type being modified */
  contentKind: "schematic" | "layout" | "symbol";

  /** Entity type being modified */
  targetKind: string;

  /** Entity ID being modified */
  entityId: number;

  /** Type of operation */
  opKind: "add" | "update" | "remove";

  /** For updates: which fields changed (for field-level LWW) */
  changedFields?: string[];

  /** The operation payload */
  data?: unknown;

  /** Server version this operation was based on */
  baseVersion: number;
}

/**
 * Request to apply CRDT operations.
 */
export interface CRDTChangesRequest {
  /** Client making the request */
  clientId: string;

  /** Client's current Lamport clock counter */
  clientClock: number;

  /** Last known server version */
  baseVersion: number;

  /** Operations to apply */
  operations: CRDTOp[];
}

/**
 * Response from applying CRDT operations.
 */
export interface CRDTChangesResponse {
  /** New server version after applying operations */
  serverVersion: number;

  /** Server's Lamport clock counter */
  serverClock: number;

  /** Operations that were successfully applied (may be transformed) */
  applied: CRDTOp[];

  /** Operations that were rejected */
  rejected?: Array<{
    opId: string;
    reason: "concurrent-delete" | "version-too-old" | "invalid-state";
  }>;

  /** Operations the client missed (for catchup) */
  missedOperations?: CRDTOp[];
}

/**
 * Configuration for CRDT behavior.
 */
export interface CRDTConfig {
  /** Maximum operations to replay for catchup (beyond this, send full state) */
  maxCatchupOps: number;

  /** How long to keep tombstones before GC (0 = session-scoped) */
  tombstoneRetentionMs: number;
}

/**
 * Default CRDT configuration.
 */
export const DEFAULT_CRDT_CONFIG: CRDTConfig = {
  maxCatchupOps: 100,
  tombstoneRetentionMs: 0, // Session-scoped: GC when all clients disconnect
};

//
// # Runtime Validators
//
// Validators for CRDT wire format types, used at data boundaries.
//

/** Validator for LamportTimestamp */
export const LamportTimestampValidator = {
  validator: new Validator(typia.createValidate<LamportTimestamp>()),
};

/** Validator for CRDTOp */
export const CRDTOpValidator = {
  validator: new Validator(typia.createValidate<CRDTOp>()),
};

/** Validator for CRDTChangesRequest */
export const CRDTChangesRequestValidator = {
  validator: new Validator(typia.createValidate<CRDTChangesRequest>()),
};

/** Validator for CRDTChangesResponse */
export const CRDTChangesResponseValidator = {
  validator: new Validator(typia.createValidate<CRDTChangesResponse>()),
};
